/**
 * CropEditor — library-free, pointer-driven crop frame over an original image.
 *
 * The frame is locked to a target aspect ratio (`aspect = w/h`). The operator
 * drags the box to move it and the four corner handles to resize it; the box is
 * clamped inside the image and re-fitted to keep the ratio. On every gesture we
 * emit a NORMALISED rect `{x,y,w,h}` (0..1 of the original) — the same shape the
 * backend stores in media_crops and the client replays via CSS object-fit/scale.
 *
 * Pointer math is done in stage-relative fractions so it is resolution- and
 * zoom-independent; nothing depends on the natural pixel size of the source.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CropRect } from "../../hooks/useOperatorProfile";
import css from "./anketa.module.css";

type Corner = "nw" | "ne" | "sw" | "se";
type Mode = { kind: "move" } | { kind: "resize"; corner: Corner } | null;

const MIN = 0.08; // minimum crop side as a fraction of the image

/** Default centred crop of the given aspect, as large as the image allows. */
export function defaultCrop(aspect: number): CropRect {
  // Fit the largest aspect-correct box inside a 1×1 image.
  let w = 1;
  let h = w / aspect;
  if (h > 1) { h = 1; w = h * aspect; }
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

function clamp01(v: number): number { return Math.max(0, Math.min(1, v)); }

export function CropEditor({
  src,
  aspect,
  value,
  onChange,
}: {
  src: string;
  aspect: number;
  value: CropRect | undefined;
  onChange: (rect: CropRect) => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  /** Current rect — controlled by `value` but tracked locally during drag. */
  const [rect, setRect] = useState<CropRect>(() => value ?? defaultCrop(aspect));
  const mode = useRef<Mode>(null);
  /** Stage aspect (img natural ratio mapped to rendered px) for ratio-correct resize. */
  const stageAspect = useRef<number>(1);

  // Sync down when the parent value or target aspect changes (e.g. switch usage).
  useEffect(() => {
    setRect(value ?? defaultCrop(aspect));
  }, [value, aspect]);

  const measureStage = useCallback(() => {
    const el = stageRef.current;
    if (el && el.clientHeight > 0) stageAspect.current = el.clientWidth / el.clientHeight;
  }, []);

  useEffect(() => {
    measureStage();
    window.addEventListener("resize", measureStage);
    return () => window.removeEventListener("resize", measureStage);
  }, [measureStage]);

  /** Pointer position → stage fraction (0..1, clamped). */
  const pointFrac = useCallback((e: PointerEvent | React.PointerEvent): { fx: number; fy: number } => {
    const el = stageRef.current;
    if (!el) return { fx: 0, fy: 0 };
    const r = el.getBoundingClientRect();
    return {
      fx: clamp01((e.clientX - r.left) / r.width),
      fy: clamp01((e.clientY - r.top) / r.height),
    };
  }, []);

  const start = useRef<{ fx: number; fy: number; rect: CropRect }>({ fx: 0, fy: 0, rect });

  const onPointerDown = (e: React.PointerEvent, m: Mode) => {
    e.preventDefault();
    e.stopPropagation();
    measureStage();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    mode.current = m;
    const p = pointFrac(e);
    start.current = { fx: p.fx, fy: p.fy, rect };
  };

  useEffect(() => {
    function move(e: PointerEvent) {
      const m = mode.current;
      if (!m) return;
      const p = pointFrac(e);
      const s = start.current;
      if (m.kind === "move") {
        const nx = clamp01(s.rect.x + (p.fx - s.fx));
        const ny = clamp01(s.rect.y + (p.fy - s.fy));
        // keep box fully inside
        const x = Math.min(nx, 1 - s.rect.w);
        const y = Math.min(ny, 1 - s.rect.h);
        const next = { ...s.rect, x: Math.max(0, x), y: Math.max(0, y) };
        setRect(next);
        onChange(next);
      } else {
        const next = resizeRect(s.rect, m.corner, p.fx, p.fy, aspect, stageAspect.current);
        setRect(next);
        onChange(next);
      }
    }
    function up(e: PointerEvent) {
      if (mode.current) {
        (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
        mode.current = null;
      }
    }
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [aspect, onChange, pointFrac]);

  const boxStyle: React.CSSProperties = {
    left: `${rect.x * 100}%`,
    top: `${rect.y * 100}%`,
    width: `${rect.w * 100}%`,
    height: `${rect.h * 100}%`,
  };

  return (
    <div ref={stageRef} className={css.cropStage} onLoad={measureStage}>
      <img className={css.cropImg} src={src} alt="" draggable={false} onLoad={measureStage} />
      <div className={css.cropBox} style={boxStyle} onPointerDown={(e) => onPointerDown(e, { kind: "move" })}>
        <span className={`${css.cropHandle} ${css.hNW}`} onPointerDown={(e) => onPointerDown(e, { kind: "resize", corner: "nw" })} />
        <span className={`${css.cropHandle} ${css.hNE}`} onPointerDown={(e) => onPointerDown(e, { kind: "resize", corner: "ne" })} />
        <span className={`${css.cropHandle} ${css.hSW}`} onPointerDown={(e) => onPointerDown(e, { kind: "resize", corner: "sw" })} />
        <span className={`${css.cropHandle} ${css.hSE}`} onPointerDown={(e) => onPointerDown(e, { kind: "resize", corner: "se" })} />
      </div>
    </div>
  );
}

/**
 * Resize from a corner while preserving `aspect` (the crop's display ratio).
 * `stageAspect` (stageW/stageH) converts the height delta into the same
 * fractional space as width so a 1:1 crop stays visually square regardless of
 * the source image's own proportions.
 */
function resizeRect(
  base: CropRect,
  corner: Corner,
  fx: number,
  fy: number,
  aspect: number,
  stageAspect: number,
): CropRect {
  // Anchor = opposite corner (stays fixed).
  const right = base.x + base.w;
  const bottom = base.y + base.h;
  const anchorX = corner === "nw" || corner === "sw" ? right : base.x;
  const anchorY = corner === "nw" || corner === "ne" ? bottom : base.y;

  // New width from horizontal distance to the anchor.
  let w = Math.abs(fx - anchorX);
  // Height that matches `aspect` in *visual* terms: w(px)/h(px)=aspect →
  // (w*stageW)/(h*stageH)=aspect → h = w * (stageAspect / aspect).
  let h = w * (stageAspect / aspect);

  // Enforce minimum.
  if (w < MIN) { w = MIN; h = w * (stageAspect / aspect); }
  if (h < MIN) { h = MIN; w = h * (aspect / stageAspect); }

  // Position so the anchor corner is fixed.
  let x = corner === "nw" || corner === "sw" ? anchorX - w : anchorX;
  let y = corner === "nw" || corner === "ne" ? anchorY - h : anchorY;

  // Clamp inside the image; if clamped, shrink to fit while keeping ratio.
  if (x < 0) { w += x; h = w * (stageAspect / aspect); x = 0; }
  if (y < 0) { h += y; w = h * (aspect / stageAspect); y = 0; }
  if (x + w > 1) { w = 1 - x; h = w * (stageAspect / aspect); }
  if (y + h > 1) { h = 1 - y; w = h * (aspect / stageAspect); }
  // Re-anchor after ratio re-fit so the dragged corner tracks correctly.
  if (corner === "nw" || corner === "sw") x = Math.max(0, anchorX - w);
  if (corner === "nw" || corner === "ne") y = Math.max(0, anchorY - h);

  return {
    x: clamp01(x),
    y: clamp01(y),
    w: Math.max(MIN, Math.min(1, w)),
    h: Math.max(MIN, Math.min(1, h)),
  };
}

/**
 * CSS-only live preview of one crop, exactly how the client replays it:
 * a fixed-aspect frame + absolutely-positioned <img> scaled so the crop region
 * fills the frame. `object-position` is computed from the crop offset.
 */
export function cropPreviewStyle(rect: CropRect | undefined, filterCss?: string): React.CSSProperties {
  const r = rect ?? { x: 0, y: 0, w: 1, h: 1 };
  // Scale the image up so the crop's width/height fills 100% of the frame.
  const scaleX = 1 / Math.max(r.w, 0.0001);
  const scaleY = 1 / Math.max(r.h, 0.0001);
  // Translate so the crop's top-left aligns to the frame origin (percentages of
  // the *scaled* image). Using width/height 100% + object-fit:cover on the frame
  // gets distorted, so we drive size via width/height % and offset via left/top.
  return {
    width: `${scaleX * 100}%`,
    height: `${scaleY * 100}%`,
    left: `${-r.x * scaleX * 100}%`,
    top: `${-r.y * scaleY * 100}%`,
    objectFit: "cover",
    filter: filterCss,
  };
}
